import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../api-base-url.token';

export interface PatientMessage {
  id: string;
  titre: string;
  description: string;
  auteur: string;
  createdAt: string;
  canDelete: boolean;
  titreI18n?: string;
  descriptionI18n?: string;
  imageContentType?: string;
  establishmentIds?: string[];
  categorie?: string;
  /** Patient Demand (V2A.6-Bis) : si true, le mobile affiche le bloc "Ca m'interesse"/"Pas maintenant" sur cette actualite. */
  prevention?: boolean;
  /** Code sujet prevention.prevention_topics (ex. "SPORT") -- ignore si prevention=false. */
  topicCode?: string;
}

export interface PagedResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
}

/** Sujet Patient Demand actif (prevention.prevention_topics) -- pour le sélecteur du composeur. */
export interface ActivePreventionTopic {
  code: string;
  label: string;
}

@Injectable({ providedIn: 'root' })
export class PatientMessagesService {
  private http = inject(HttpClient);
  private base = inject(API_BASE_URL);

  getMessages(establishmentId: string, page: number = 0, size: number = 10): Observable<PagedResponse<PatientMessage>> {
    const params = {
      page: page.toString(),
      size: size.toString()
    };

    return this.http.get<PagedResponse<PatientMessage>>(
      `${this.base}/api/v1/establishments/${establishmentId}/patient-messages`,
      { params }
    );
  }

  getImage(establishmentId: string, messageId: string): Observable<Blob> {
    return this.http.get(
      `${this.base}/api/v1/establishments/${establishmentId}/patient-messages/${messageId}/image`,
      { responseType: 'blob' }
    );
  }

  createMessage(establishmentId: string, data: { titre: string; description: string; titreI18n?: Record<string, string>; descriptionI18n?: Record<string, string>; imageData?: string; categorie?: string; prevention?: boolean; topicCode?: string }): Observable<PatientMessage> {
    return this.http.post<PatientMessage>(
      `${this.base}/api/v1/establishments/${establishmentId}/patient-messages`,
      data
    );
  }

  deleteMessage(establishmentId: string, messageId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/api/v1/establishments/${establishmentId}/patient-messages/${messageId}`
    );
  }

  updateMessage(establishmentId: string, messageId: string, data: { titre: string; description: string; titreI18n?: Record<string, string>; descriptionI18n?: Record<string, string>; imageData?: string; categorie?: string; prevention?: boolean; topicCode?: string }): Observable<PatientMessage> {
    return this.http.put<PatientMessage>(
      `${this.base}/api/v1/establishments/${establishmentId}/patient-messages/${messageId}`,
      data
    );
  }

  createMessageMulti(establishmentIds: string[], data: { titre: string; description: string; titreI18n?: Record<string, string>; descriptionI18n?: Record<string, string>; imageData?: string; categorie?: string; prevention?: boolean; topicCode?: string }): Observable<{ count: number }> {
    return this.http.post<{ count: number }>(
      `${this.base}/api/v1/patient-messages/multi`,
      { establishment_ids: establishmentIds, ...data }
    );
  }

  /** Sujets Patient Demand actifs, pour le sélecteur du composeur (jamais un catalogue de gestion). */
  getActivePreventionTopics(): Observable<ActivePreventionTopic[]> {
    return this.http.get<ActivePreventionTopic[]>(`${this.base}/api/v1/prevention/topics`);
  }
}
